// File: frontend/src/hooks/useCollapsedOldEntries.ts

import { useState, useCallback, useEffect, useRef } from "react";
import { EntryModel } from "../models";
import { useAuth } from "../context/AuthContext";

// Generic page fetcher supplied by each domain page (match, person, club, team, custom topic).
// Must fetch with sort="oldest" and return entries together with pagination totals.
export type CollapseEntryFetcher = (
  page: number,
  size: number,
  filterToday: boolean
) => Promise<{ entries: EntryModel[]; totalItems: number; totalPages: number }>;

// Safety cap: if a topic somehow has more entries written today than this,
// collapse mode is skipped and the page falls back to the normal list.
const MAX_TODAY_ENTRIES = 500;

/**
 * When a topic already has old entries AND at least one entry was written today,
 * the initial view shows only today's entries with a "{count} entry daha" bar on
 * top. Clicking the bar reveals the previous entries chunk by chunk (page-sized),
 * the chunk closest to today first, inserted right below the bar.
 *
 * Collapse is only active in the default view: sort=oldest, no today/rookie
 * filter, no keyword. Any toolbar interaction should set `eligible` to false,
 * which disables collapse and the page falls back to the classic list behaviour.
 *
 * "Today" is decided by the backend (filterToday param), so the boundary is
 * always consistent with the server's day — no client-side timezone math.
 */
export const useCollapsedOldEntries = (
  targetId: number | null,
  eligible: boolean,
  entriesPerPage: number,
  fetcher: CollapseEntryFetcher,
  // Optional: forces the detection pass to run again whenever the value changes.
  // Used by pages whose left-pane row can be clicked while that same page is
  // already open — the URL stays identical, so nothing else in the dependency
  // list changes and entries written in the meantime stayed invisible.
  reloadToken: number = 0
) => {
  const { user } = useAuth();

  const [active, setActive]               = useState(false);
  const [checking, setChecking]           = useState(false);
  const [entries, setEntries]             = useState<EntryModel[]>([]);
  const [hiddenCount, setHiddenCount]     = useState(0);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  // Next full-list page (1-based, oldest sort) to load when the bar is clicked.
  // Older entries occupy pages 1..ceil(olderCount / perPage) of the unfiltered list.
  const nextOlderPageRef = useRef(0);

  // Refs keep the latest values available inside stable callbacks
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const perPageRef = useRef(entriesPerPage);
  perPageRef.current = entriesPerPage;
  const entriesRef = useRef<EntryModel[]>([]);
  entriesRef.current = entries;

  useEffect(() => {
    let cancelled = false;

    const detect = async () => {
      // Not applicable — reset to inactive so the page renders the normal list
      if (!targetId || !eligible) {
        setActive(false);
        setChecking(false);
        setEntries([]);
        setHiddenCount(0);
        return;
      }

      setChecking(true);
      try {
        // Step 1: today's entries (first page) + today's total count
        const todayRes = await fetcherRef.current(1, perPageRef.current, true);
        if (cancelled) return;
        const todayCount = todayRes.totalItems;
        if (todayCount === 0 || todayCount > MAX_TODAY_ENTRIES) {
          setActive(false);
          return;
        }

        // Step 2: total entry count of the topic (size=1 keeps the payload tiny)
        const allRes = await fetcherRef.current(1, 1, false);
        if (cancelled) return;
        const olderCount = allRes.totalItems - todayCount;
        if (olderCount <= 0) {
          // Every entry was written today — nothing to collapse
          setActive(false);
          return;
        }

        // Step 3: make sure we hold ALL of today's entries
        // (a single extra request only when today spans more than one page)
        let todayEntries = todayRes.entries;
        if (todayCount > todayEntries.length) {
          const fullToday = await fetcherRef.current(1, todayCount, true);
          if (cancelled) return;
          todayEntries = fullToday.entries;
        }

        nextOlderPageRef.current = Math.ceil(olderCount / perPageRef.current);
        setEntries(todayEntries);
        setHiddenCount(olderCount);
        setActive(true);
      } catch {
        // On any failure fall back silently to the normal list
        if (!cancelled) setActive(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    detect();
    return () => { cancelled = true; };
  // Re-detect when the topic or the viewer changes, or when the default view is restored
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, eligible, user?.id, reloadToken]);

  /** Loads the previous (older) chunk and inserts it right below the bar. */
  const loadOlderEntries = useCallback(async () => {
    if (isLoadingOlder || nextOlderPageRef.current < 1) return;
    setIsLoadingOlder(true);
    try {
      const res = await fetcherRef.current(nextOlderPageRef.current, perPageRef.current, false);
      // The boundary page can contain some of today's entries — skip duplicates
      const known = new Set(entriesRef.current.map(e => e.id));
      const fresh = res.entries.filter(e => !known.has(e.id));
      // Keep chronological order: revealed older entries go above the current list
      setEntries(prev => [...fresh, ...prev]);
      nextOlderPageRef.current -= 1;
      setHiddenCount(prev =>
        nextOlderPageRef.current < 1 ? 0 : Math.max(0, prev - fresh.length)
      );
    } catch {
      // Silent failure — the bar stays visible and can be clicked again
    } finally {
      setIsLoadingOlder(false);
    }
  }, [isLoadingOlder]);

  // Same signature as the domain hooks' updateEntries so pages can apply
  // vote / delete / new-post mutations to the collapsed list as well
  const updateEntries = useCallback(
    (callback: (prev: EntryModel[]) => EntryModel[]) => setEntries(callback),
    []
  );

  return {
    active,          // true → the page should render `entries` from this hook
    checking,        // true while the initial detection requests are running
    entries,         // today's entries + any expanded older chunks (oldest first)
    hiddenCount,     // how many older entries are still hidden behind the bar
    isLoadingOlder,  // loading state for the bar click
    loadOlderEntries,
    updateEntries,
  };
};